import { DecoratorNode } from 'lexical';

/**
 * The two pills of the formula editor (FormulaInput.js; split out of it, KPR-708,
 * to keep that file under its size ceiling): a REFERENCE pill, `{{token}}`, and a
 * VALUE pill, a unit or date part picked for a function's argument. Both are
 * Lexical decorator nodes whose text content IS what the formula stores.
 */


// The words on a pill come from the caller's vocabulary, which belongs to the
// React component, not to the node: a renamed measure must relabel every pill
// at once, and an undo must draw a restored pill with today's words. So the
// node keeps only its token, and createDOM asks the editor's label function.
export const labelsByEditor = new WeakMap();
// A TEMPLATE editor (KPR-708) draws each pill's words as a button that edits
// the pill (its transform, its value): which editors do is set once, at their
// creation, because the node's DOM is made by the node.
export const editableByEditor = new WeakSet();
const labelOf = (editor, token) => {
	const labelFor = labelsByEditor.get(editor);
	return labelFor ? labelFor(token) : token.slice(2, -2).trim() || token;
};

/** Writes a pill's words, and its remove button's name, into its DOM. */
export function paintPill(dom, label) {
	const text = dom.querySelector('.token-input__pill-label');
	if (text && text.textContent !== label) text.textContent = label;
	const remove = dom.querySelector('[data-token-remove]');
	const name = `Remove ${label}`;
	if (remove && remove.getAttribute('aria-label') !== name) remove.setAttribute('aria-label', name);
	const edit = dom.querySelector('[data-token-edit]');
	const editName = `Change ${label}`;
	if (edit && edit.getAttribute('aria-label') !== editName) edit.setAttribute('aria-label', editName);
}

export class FormulaRefNode extends DecoratorNode {
	static getType() {
		return 'keepr-formula-ref';
	}

	static clone(node) {
		return new FormulaRefNode(node.__token, node.__key);
	}

	static importJSON(json) {
		return $createFormulaRefNode(json.token);
	}

	constructor(token, key) {
		super(key);
		this.__token = token;
	}

	exportJSON() {
		return { ...super.exportJSON(), type: FormulaRefNode.getType(), version: 1, token: this.__token };
	}

	getToken() {
		return this.getLatest().__token;
	}

	// The node's text IS the stored reference: serialising, copying and the
	// undo stack all read the formula exactly as it is stored.
	getTextContent() {
		return this.__token;
	}

	isInline() {
		return true;
	}

	// false: an arrow key steps OVER the pill (one press, as over a character)
	// instead of first selecting it as a node — a text field has no "selected
	// pill" state, and a second press to get past one is the bounce KPR-333
	// described.
	isKeyboardSelectable() {
		return false;
	}

	// The same DOM TokenInput drew, so the pill CSS, the chart builder's
	// narrow-screen rules and every `[data-token]` query carry over.
	createDOM(config, editor) {
		const doc = document;
		const pill = doc.createElement('span');
		pill.className = 'token-input__pill';
		// Lexical sets the property too; the attribute is what every engine
		// (and jsdom, which has no contentEditable property) reads the same way.
		pill.setAttribute('contenteditable', 'false');
		pill.setAttribute('data-token', this.__token);
		const editable = editableByEditor.has(editor);
		const text = doc.createElement(editable ? 'button' : 'span');
		text.className = 'token-input__pill-label';
		if (editable) {
			text.setAttribute('type', 'button');
			text.setAttribute('data-token-edit', '');
			text.classList.add('token-input__pill-edit');
		}
		pill.appendChild(text);
		const remove = doc.createElement('button');
		remove.setAttribute('type', 'button');
		remove.setAttribute('data-token-remove', '');
		remove.className = 'token-input__pill-remove';
		remove.textContent = '×';
		pill.appendChild(remove);
		paintPill(pill, labelOf(editor, this.__token));
		return pill;
	}

	// A token never changes on a node (a different reference is a new node),
	// and a relabel patches the DOM in place (see FormulaInput's effect).
	updateDOM() {
		return false;
	}

	decorate() {
		return null;
	}
}

export function $createFormulaRefNode(token) {
	return new FormulaRefNode(token);
}

export function $isFormulaRefNode(node) {
	return node instanceof FormulaRefNode;
}

/**
 * A VALUE pill (KPR-350): a unit or a date part picked for a function's
 * argument — `convert({{weight}}, "kg")` draws "Kilograms". Its text content
 * IS the literal exactly as stored (`"kg"`, or `'kg'` when that is how it was
 * written), so the formula stays a plain string (d03) and reads back byte for
 * byte. The words are the choice's label, kept on the node: they belong to the
 * argument the literal was picked for, not to the text (`"m"` is Months to
 * dateDiff and metres to the units registry). It is not an element, so it
 * carries `data-choice`, never `data-token`: the element vocabulary never
 * relabels it, and nothing that counts element pills counts it.
 */
export class FormulaChoiceNode extends DecoratorNode {
	static getType() {
		return 'keepr-formula-choice';
	}

	static clone(node) {
		return new FormulaChoiceNode(node.__literal, node.__label, node.__key);
	}

	static importJSON(json) {
		return $createFormulaChoiceNode(json.literal, json.label);
	}

	constructor(literal, label, key) {
		super(key);
		this.__literal = literal;
		this.__label = label;
	}

	exportJSON() {
		return { ...super.exportJSON(), type: FormulaChoiceNode.getType(), version: 1, literal: this.__literal, label: this.__label };
	}

	getLiteral() {
		return this.getLatest().__literal;
	}

	getTextContent() {
		return this.__literal;
	}

	isInline() {
		return true;
	}

	// Stepped over in one arrow press, as an element pill is (KPR-333).
	isKeyboardSelectable() {
		return false;
	}

	createDOM() {
		const doc = document;
		const pill = doc.createElement('span');
		pill.className = 'token-input__pill formula-input__pill--value';
		pill.setAttribute('contenteditable', 'false');
		pill.setAttribute('data-choice', this.__literal.slice(1, -1));
		// What the formula keeps, one hover away (it is never required reading).
		pill.setAttribute('title', this.__literal);
		const text = doc.createElement('span');
		text.className = 'token-input__pill-label';
		pill.appendChild(text);
		const remove = doc.createElement('button');
		remove.setAttribute('type', 'button');
		remove.setAttribute('data-token-remove', '');
		remove.className = 'token-input__pill-remove';
		remove.textContent = '×';
		pill.appendChild(remove);
		paintPill(pill, this.__label || this.__literal.slice(1, -1));
		return pill;
	}

	updateDOM() {
		return false;
	}

	decorate() {
		return null;
	}
}

export function $createFormulaChoiceNode(literal, label) {
	return new FormulaChoiceNode(literal, label);
}

export function $isFormulaChoiceNode(node) {
	return node instanceof FormulaChoiceNode;
}
